import { useCallback, useEffect, useMemo, useState, useRef } from 'react';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import { useManualQuery, useMutation } from '@zenbusiness/application-commons-graphql';
import isUndefinedOrNull from '@zenbusiness/application-commons-utils/isUndefinedOrNull';
import Box from '@zenbusiness/zui/Box';
import CircularProgress from '@zenbusiness/zui/CircularProgress';
import Heading from '@zenbusiness/zui/Heading';
import IconButton from '@zenbusiness/zui/IconButton';
import LoadingButton from '@zenbusiness/zui/LoadingButton';
import Radio from '@zenbusiness/zui/Radio';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Skeleton from '@zenbusiness/zui/Skeleton';
import Stack from '@zenbusiness/zui/Stack';
import { styled } from '@zenbusiness/zui/styles';
import Text from '@zenbusiness/zui/Text';
import TextField from '@zenbusiness/zui/TextField';
import Tooltip from '@zenbusiness/zui/Tooltip';
import PropTypes from 'prop-types';
import { useNavigate, useSearchParams, generatePath } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { FORM_FIELD_NAME } from '#client/components/AddCounterPartyModal/constants/fields';
import {
  NOT_SUPPORT_WIRE_MESSAGE,
  ROUTING_NUMBER_ERROR_MESSAGE
} from '#client/components/AddCounterPartyModal/constants/messages';
import { validateAccountErrorMessage } from '#client/components/AddCounterPartyModal/utils/validators';
import GoogleMapField from '#client/components/GoogleMapField';
import { FocusGrid, ContainerGrid, ActionButtonsGrid } from '#client/components/Grid';
import LoadingContainerSpinner from '#client/components/LoadingContainerSpinner';
import { SendMoneyInput } from '#client/components/MoneyInputs';
import { INPUT_TYPES } from '#client/components/MoneyInputs/constants/types';
import { formatMoneyDisplay } from '#client/components/MoneyInputs/utils/format';
import ACHAlert from '#client/components/MoveMoney/components/ACHAlert';
import MoneyAlert from '#client/components/MoveMoney/components/MoneyAlert';
import TransactionDescription from '#client/components/MoveMoney/components/TransactionDescription';
import WireAlert from '#client/components/MoveMoney/components/WireAlert';
import WireTransactionDescription from '#client/components/MoveMoney/components/WireTransactionDescription';
import { forceLoginWireTransfer } from '#client/components/MoveMoney/utils/common';
import ReadOrEdit from '#client/components/ReadOrEdit';
import FieldReadView from '#client/components/ReadOrEdit/FieldReadView';
import FieldWithEditable from '#client/components/ReadOrEdit/FieldWithEditable';
import ViewAllTransactionLimitButton from '#client/components/ViewAllTransactionLimitButton';
import { COUNTERPARTY_CONNECTED_TYPE } from '#client/constants/counterParty';
import { COUNTERPARTY_ID, MFA_VERIFIED, MODAL_NAMES } from '#client/constants/modals';
import { TOKEN_TYPES } from '#client/constants/tokens';
import {
  IS_THAT_CORRECT_ACH_STEP,
  TRANSFER_TYPE,
  WHICH_ACCOUNT_STEP
} from '#client/constants/transfer';
import useBankingAccounts from '#client/hooks/useBankingAccounts';
import useBankingPath from '#client/hooks/useBankingPath';
import useCounterpartyDetails from '#client/hooks/useCounterpartyDetails';
import useCustomerToken from '#client/hooks/useCustomerToken';
import { useDisableWireInZBApp } from '#client/hooks/useDisableWireInZBApp';
import useEnterSubmit from '#client/hooks/useEnterSubmit';
import useFormStep from '#client/hooks/useFormStep';
import useModalContext from '#client/hooks/useModalContext';
import useModalDisplayTracker from '#client/hooks/useModalDisplayTracker';
import useSessionStorage from '#client/hooks/useSessionStorage';
import useToastContext from '#client/hooks/useToastContext';
import useUnitAccount from '#client/hooks/useUnitAccount';
import useUnitAccountLimits from '#client/hooks/useUnitAccountLimits';
import { UPDATE_COUNTER_PARTY_STATUS_MUTATION } from '#client/mutations';
import { INSTITUTION_QUERY } from '#client/queries';
import { formatNumber } from '#client/utils/currency';
import { capitalizeFirstLetter, getLast4digits } from '#client/utils/text';

import {
  FieldContainerStyled,
  RadioGroupStyled,
  FormControlLabelStyled,
  DialogContentStyled,
  Wrapper,
  DisableWireBoxStyled,
  TooltipStyle,
  LinkStyled
} from './HowMuchForm.styles';

const infoIcon = (
  <SharedAsset
    alt=""
    src="/s2/icon/c-info-outline.svg"
    width={16}
  />
);
const checkIcon = (
  <SharedAsset
    alt=""
    src="/s2/icon/check-circle-fill.svg"
  />
);

const TooltipStyled = styled(({ className, ...props }) => (
  <Tooltip
    {...props}
    classes={{ popper: className }}
  />
))(TooltipStyle);

const InfoToolTip = ({ message, sx }) => (
  <TooltipStyled title={message}>
    <IconButton
      label="info"
      sx={sx}
    >
      {infoIcon}
    </IconButton>
  </TooltipStyled>
);

InfoToolTip.propTypes = {
  message: PropTypes.string,
  sx: PropTypes.shape({
    marginTop: PropTypes.string
  })
};

const BankName = ({ loading, name }) => {
  if (loading) {
    return (
      <CircularProgress
        color="secondary"
        size={20}
      />
    );
  }

  if (!name) {
    return null;
  }

  return (
    <Stack
      alignItems="center"
      direction="row"
    >
      <Stack
        alignItems="center"
        direction="row"
        spacing="4px"
      >
        <Text
          size="md"
          sx={{ width: 'max-content' }}
          weight="medium"
        >
          {name}
        </Text>
        {checkIcon}
      </Stack>
    </Stack>
  );
};

BankName.propTypes = {
  name: PropTypes.string,
  loading: PropTypes.bool
};

const CHASE_BANK = 'jpmorgan chase bank';

export default function HowMuchForm({
  handleClose = null,
  handleOpenDepositModal = null,
  openAddPayeeModal = null
}) {
  const moneyInputRef = useRef(null);
  const navigate = useNavigate();
  const { clearAllModal } = useModalContext();
  const { id: bankingAccountId, loading: isFetchingBankingAccounts } = useBankingAccounts();

  const [, setCounterPartyStorage] = useSessionStorage('counterParty', {});
  const { isDisable: isDisableWireInZBApp } = useDisableWireInZBApp();
  const [isEditHandlerTransferType, setIsEditHandlerTransferType] = useState(false);
  const [wireRoutingNumber, setWireRoutingNumber] = useState('');
  const [institutionName, setInstitutionName] = useState('');
  const [wireAddress, setWireAddress] = useState({});
  const [wireRoutingNumberError, setWireRoutingNumberError] = useState('');
  const [isHandlingVerifyAccount, setIsHandlingVerifyAccount] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { setCurrentFormStep, setData, data } = useFormStep();
  const bankingPath = useBankingPath();

  const { amount, selectedToAccount, description, idempotencyKey, entryPage } = data;
  const { id, isACHSupported, isWireSupported } = selectedToAccount || {};
  const isChaseBank =
    selectedToAccount?.bank?.toLowerCase() === CHASE_BANK &&
    selectedToAccount?.connectedType === COUNTERPARTY_CONNECTED_TYPE.PLAID;
  const counterpartyId = searchParams.get(COUNTERPARTY_ID);

  // Initialize the isFetching state based on the presence of counterpartyId
  // and whether the selectedToAccount object has no keys.
  const [isFetching, setIsFetching] = useState(
    !!(
      (
        counterpartyId && // Check if counterpartyId is truthy
        Object.keys(selectedToAccount).length === 0
      ) // Check if selectedToAccount is an empty object
    )
  );

  const { counterPartyData, fetchingCounterparty, counterpartyError } = useCounterpartyDetails({
    counterpartyId
  });

  const reviewButtonId = useEnterSubmit();
  const { getCustomerToken } = useCustomerToken({ type: TOKEN_TYPES.COMMON });
  const { createCommonErrorToast } = useToastContext();

  const [moneyInputState, setMoneyInputState] = useState({
    value: amount,
    isError: false
  });

  const isWireTypeOnly = isWireSupported && !isACHSupported;

  const typeTransferSelected = isWireTypeOnly
    ? TRANSFER_TYPE.WIRE.value
    : TRANSFER_TYPE.STANDARD_ACH.value;

  const [selectedType, setSelectedType] = useState(typeTransferSelected);

  const { unitAccount, loading: loadingUnitAccount, error: errorUnitAccount } = useUnitAccount();

  const {
    remains,
    loading: loadingLimit,
    error: errorLimit,
    limit,
    wireLimitsRemains,
    wireOutgoingFee,
    wireLimits
  } = useUnitAccountLimits();

  const wireOutgoingFeeText = `$${formatNumber(wireOutgoingFee)}`;

  const [getInstitution, { loading: loadingInstitution }] = useManualQuery(INSTITUTION_QUERY, {
    noThrow: true
  });

  useEffect(() => {
    if (counterpartyError) {
      handleClose();
    }

    /**
     * useEffect hook to update form data when conditions are met.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counterpartyError]);

  useEffect(() => {
    // Check if conditions are met for updating form data
    const shouldUpdateFormData = Boolean(
      counterpartyId &&
        counterPartyData &&
        !fetchingCounterparty &&
        Object.keys(data?.selectedToAccount).length === 0
    );

    if (shouldUpdateFormData) {
      // Update form data with selectedToAccount from counterPartyData
      setData((prevData) => ({
        ...prevData,
        // Set isFetching to false after updating form data
        selectedToAccount: counterPartyData
      }));
      setIsFetching(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counterPartyData, counterpartyId, fetchingCounterparty, data?.selectedToAccount]);

  const [updateCounterPartyWire, { loading: updateWireLoading }] = useMutation(
    UPDATE_COUNTER_PARTY_STATUS_MUTATION,
    {
      noThrow: true
    }
  );
  useModalDisplayTracker({
    elementName: 'input_amount_sm_modal',
    elementText: 'input_amount_sm_modal',
    appPageName: 'active_banking_dashboard'
  });
  const isWireTypeSelected = selectedType === TRANSFER_TYPE.WIRE.value;

  const isLoading =
    loadingLimit ||
    loadingUnitAccount ||
    updateWireLoading ||
    loadingInstitution ||
    isFetchingBankingAccounts ||
    isHandlingVerifyAccount;
  const isError =
    errorLimit || errorUnitAccount || moneyInputState.isError || wireRoutingNumberError;
  const isInvalidWireHasInput =
    isWireTypeSelected &&
    (!description || !wireAddress.valid || !wireRoutingNumber || !institutionName);

  const isInvalidWireNoInput = isWireTypeSelected && !description;

  /**
   * Check if wire input should be shown
   * - If isEnableEditPayeeFunction is true, isShowWireInput is true if isWireSupported is false
   * - If isEnableEditPayeeFunction is false, isShowWireInput is true if !data?.selectedToAccount?.wire?.address || !data?.selectedToAccount?.wire?.routingNumber
   */
  const isShowWireInput = !isWireSupported;

  const isDisabledReviewTransferHasWireInput = Boolean(
    isLoading || isError || !moneyInputState.value || isInvalidWireHasInput
  );

  const isDisabledReviewTransferNoWireInput = Boolean(
    isLoading || isError || !moneyInputState.value || isInvalidWireNoInput
  );

  const isDisabledReviewTransfer = isShowWireInput
    ? isDisabledReviewTransferHasWireInput
    : isDisabledReviewTransferNoWireInput;

  const { dailyCreditRemain, monthlyCreditRemain } = remains;
  const { dailyCredit, monthlyCredit } = limit;

  const defaultTransferType = useMemo(() => {
    switch (true) {
      case isACHSupported:
        return TRANSFER_TYPE.STANDARD_ACH;
      case isWireSupported:
        return {
          title: `${wireOutgoingFeeText} - ${TRANSFER_TYPE.WIRE.title}`,
          value: isDisableWireInZBApp
            ? `${TRANSFER_TYPE.WIRE.value} Not Available in App`
            : TRANSFER_TYPE.WIRE.value
        };
      default:
        return TRANSFER_TYPE.STANDARD_ACH;
    }
  }, [isACHSupported, isDisableWireInZBApp, isWireSupported, wireOutgoingFeeText]);

  const defaultTransferTypeOption = useMemo(() => defaultTransferType, [defaultTransferType]);

  const trackReviewTransferClick = useButtonPressedCallback({
    elementName: 'review_or_verify_transfer',
    elementText: 'Review transfer',
    appPageName: 'input_amount_sm_modal'
  });

  const trackVerifyTransferClick = useButtonPressedCallback({
    elementName: 'review_or_verify_transfer',
    elementText: 'Next',
    appPageName: 'input_amount_sm_modal'
  });

  const {
    availableBalance: accountAvailableBalance,
    accountNumber: fromAccountNumber = '',
    depositProduct: fromAccountType = ''
  } = unitAccount || {};
  const handleReviewTransfer = () => {
    trackReviewTransferClick();
    // Validate money input (empty, limit)
    const isValid = moneyInputRef.current.validate();
    if (isValid) {
      setData({
        ...data,
        amount: moneyInputState.value,
        fromAccount: unitAccount,
        transferType: selectedType,
        isDisableBack: true,
        wireOutgoingFeeText,
        idempotencyKey: idempotencyKey || uuidv4()
      });

      setCurrentFormStep(IS_THAT_CORRECT_ACH_STEP);
    }
  };

  const handleOnChangeType = (e) => {
    const { value } = e.target;
    setSelectedType(value);
    if (value !== TRANSFER_TYPE.WIRE.value) {
      setInstitutionName('');
      setWireRoutingNumber('');
      setWireRoutingNumberError('');
      setWireAddress('');
    }
  };

  const handleVerifyAccount = async () => {
    trackVerifyTransferClick();
    // Validate money input (empty, limit)
    const isValid = moneyInputRef.current.validate();

    if (!isValid) {
      return;
    }

    setIsHandlingVerifyAccount(true);
    if (isShowWireInput) {
      const customerToken = await getCustomerToken();
      if (!customerToken) {
        return;
      }

      const { addressLine1, city, state, country, zip } = wireAddress.address;
      const { error } = await updateCounterPartyWire({
        variables: {
          input: {
            counterpartyId: data.selectedToAccount.id,
            wire: {
              routingNumber: wireRoutingNumber,
              address: {
                street: addressLine1,
                city,
                state,
                postalCode: zip,
                country
              }
            },
            customerToken,
            bankingAccountId,
            idempotencyKey: uuidv4()
          }
        },
        skip: updateWireLoading
      });
      if (error) {
        // Show toast error
        createCommonErrorToast({ error });
      }
    }
    const idemKey = idempotencyKey || uuidv4();
    setCounterPartyStorage({
      amount: moneyInputState.value,
      counterPartyId: selectedToAccount?.id,
      transferType: selectedType,
      idempotencyKey: idemKey,
      description,
      wireOutgoingFeeText,
      entryPage
    });

    const redirectTo = `${entryPage}?modal=${MODAL_NAMES.SEND_MONEY}&${MFA_VERIFIED}=true`;
    forceLoginWireTransfer(idemKey, redirectTo);
  };

  const handleChangeWireRoutingNumber = useCallback((event) => {
    const { value } = event.target;
    setWireRoutingNumber(value);
    setInstitutionName('');
  }, []);

  const handleBlurWireRoutingNumber = useCallback(async () => {
    const errorMessage = validateAccountErrorMessage(
      wireRoutingNumber,
      FORM_FIELD_NAME.ROUTING_NUMBER
    );
    const shouldCallAPIVerifyRoutingNumber =
      !errorMessage && wireRoutingNumber && wireRoutingNumber.length >= 9 && !loadingInstitution;
    if (shouldCallAPIVerifyRoutingNumber) {
      const { data: wireRoutingNumberdata, error } = await getInstitution({
        variables: {
          params: {
            routingNumber: wireRoutingNumber.trim()
          }
        },
        skipCache: true
      });

      // Should show error in some cases
      // 1. API has error : error ==> show error: ROUTING_NUMBER_ERROR_MESSAGE
      // 2. API has data but data.institution is null or undefined :
      // 3. API has data isWireSupported: false is not supported
      // case 1 and case 2 show error : ROUTING_NUMBER_ERROR_MESSAGE
      // case 3 show error : NOT_SUPPORT_WIRE_MESSAGE
      const shouldShowError =
        error ||
        isUndefinedOrNull(wireRoutingNumberdata?.institution) ||
        !wireRoutingNumberdata?.institution?.isWireSupported;

      // Call wire api and received error response
      if (shouldShowError) {
        setInstitutionName('');
        setWireRoutingNumberError(
          !isUndefinedOrNull(wireRoutingNumberdata?.institution) &&
            !wireRoutingNumberdata.institution.isWireSupported
            ? NOT_SUPPORT_WIRE_MESSAGE
            : ROUTING_NUMBER_ERROR_MESSAGE
        );

        return;
      }

      // Do not support wire
      if (!wireRoutingNumberdata?.institution.isWireSupported) {
        setInstitutionName('');
        setWireRoutingNumberError(NOT_SUPPORT_WIRE_MESSAGE);

        return;
      }

      // Success
      setInstitutionName(wireRoutingNumberdata?.institution?.name);
      setWireRoutingNumberError(null);

      return;
    }
    setWireRoutingNumberError(errorMessage);
    setInstitutionName('');
  }, [getInstitution, loadingInstitution, wireRoutingNumber]);

  const transferTypeIsWireOnly = [
    {
      value: TRANSFER_TYPE.WIRE.value,
      label: (
        <FieldReadView
          title={
            isDisableWireInZBApp
              ? `${TRANSFER_TYPE.WIRE.value} Not Available in App`
              : TRANSFER_TYPE.WIRE.value
          }
          value={`${wireOutgoingFeeText} - ${TRANSFER_TYPE.WIRE.title}`}
        />
      ),
      isSelectable: !isDisableWireInZBApp,
      isDisabled: isChaseBank
    }
  ].filter(({ isDisabled }) => !isDisabled);

  const transferTypeOptionsWithWire = [
    {
      label: (
        <FieldReadView
          title={TRANSFER_TYPE.STANDARD_ACH.value}
          value={TRANSFER_TYPE.STANDARD_ACH.title}
        />
      ),
      value: TRANSFER_TYPE.STANDARD_ACH.value,
      isDisabled: false,
      isSelectable: true
    },
    {
      label: (
        <FieldReadView
          title={TRANSFER_TYPE.SAME_DAY_ACH.value}
          value={TRANSFER_TYPE.SAME_DAY_ACH.title}
        />
      ),
      value: TRANSFER_TYPE.SAME_DAY_ACH.value,
      isDisabled: false,
      isSelectable: true
    },
    {
      value: TRANSFER_TYPE.WIRE.value,
      label: (
        <FieldReadView
          title={
            isDisableWireInZBApp
              ? `${TRANSFER_TYPE.WIRE.value} Not Available in App`
              : TRANSFER_TYPE.WIRE.value
          }
          value={`${wireOutgoingFeeText} - ${TRANSFER_TYPE.WIRE.title}`}
        />
      ),
      isDisabled: isChaseBank,
      isSelectable: !isDisableWireInZBApp,
      content: (
        <Box
          ml="32px"
          mt="24px"
        >
          {isWireTypeSelected && isShowWireInput && (
            <Stack spacing="8px">
              <Stack spacing="16px">
                <TextField
                  disabled={updateWireLoading || isHandlingVerifyAccount}
                  error={Boolean(wireRoutingNumberError)}
                  helperText={wireRoutingNumberError}
                  inputProps={{
                    autoComplete: 'new-password',
                    maxLength: 9,
                    'aria-label': 'Wire routing number field'
                  }}
                  // eslint-disable-next-line react/jsx-no-duplicate-props
                  InputProps={{
                    endAdornment: (
                      <InfoToolTip message="Check to see if the recipient’s bank uses a wire-specific routing number." />
                    ),
                    autoFocus: true
                  }}
                  label="Wire Routing Number"
                  onBlur={handleBlurWireRoutingNumber}
                  onChange={handleChangeWireRoutingNumber}
                  value={wireRoutingNumber}
                />
                <BankName
                  loading={loadingInstitution}
                  name={institutionName}
                />
                <GoogleMapField
                  dataChangeHandler={setWireAddress}
                  disableClearable
                  disabled={updateWireLoading || isHandlingVerifyAccount}
                  endAdornment={
                    <InfoToolTip
                      message="This address must match the address that is on file with the recipient’s bank."
                      sx={{ marginRight: '-10px' }}
                    />
                  }
                  errorMessage="Please enter a valid address"
                  sizeError="body_smallRegular"
                />
              </Stack>
              <Stack
                direction="row"
                spacing="8px"
              >
                <Box>
                  <SharedAsset
                    alt=""
                    src="/s2/icon/saved-items-outline.svg"
                    width={16}
                  />
                </Box>
                <Text size="xs">We’ll save this contact’s information for you.</Text>
              </Stack>
            </Stack>
          )}
        </Box>
      )
    }
  ].filter(({ isDisabled }) => !isDisabled);

  useEffect(() => {
    if (errorLimit || errorUnitAccount) {
      handleClose();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorLimit, errorUnitAccount]);

  const blurHandler = useCallback(
    (event) => {
      setData({
        ...data,
        description: event?.target?.value || ''
      });
    },
    [data, setData]
  );

  const transferOptions = isWireTypeOnly ? transferTypeIsWireOnly : transferTypeOptionsWithWire;

  const inputBalanceAndLimit = useMemo(() => {
    const { monthlyWireRemain, dailyWireRemain } = wireLimitsRemains || {};
    const { daily, monthly } = wireLimits || {};
    const { amount: amountBalance } = accountAvailableBalance || {};

    return {
      balance: amountBalance,
      monthlyRemainLimit: isWireTypeSelected ? monthlyWireRemain : monthlyCreditRemain,
      dailyRemainLimit: isWireTypeSelected ? dailyWireRemain : dailyCreditRemain,
      dailyLimit: isWireTypeSelected ? daily : dailyCredit,
      monthlyLimit: isWireTypeSelected ? monthly : monthlyCredit,
      inputType: isWireTypeSelected ? INPUT_TYPES.WIRE_MONEY : INPUT_TYPES.SEND_MONEY
    };
  }, [
    accountAvailableBalance,
    dailyCredit,
    dailyCreditRemain,
    isWireTypeSelected,
    monthlyCredit,
    monthlyCreditRemain,
    wireLimits,
    wireLimitsRemains
  ]);

  const editToSectionHandler = useCallback(() => {
    if (counterpartyId) {
      setSearchParams(
        (prevParams) => {
          const newParams = new URLSearchParams(prevParams);
          newParams.delete(COUNTERPARTY_ID);
          return newParams;
        },
        { replace: true }
      );
    }
    setCurrentFormStep(WHICH_ACCOUNT_STEP);
  }, [counterpartyId, setCurrentFormStep, setSearchParams]);

  const handleNavigateToSpecificPayee = useCallback(() => {
    clearAllModal();
    navigate(generatePath(`${bankingPath}/payees/:id`, { id }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankingPath, id]);

  const isShowMoneyAlert = selectedType !== TRANSFER_TYPE.WIRE.value && !isEditHandlerTransferType;

  const fromTitle = loadingUnitAccount ? (
    <Skeleton width="100%" />
  ) : (
    `ZenBusiness Banking • ${formatMoneyDisplay(inputBalanceAndLimit.balance / 100)}`
  );
  const fromValue = loadingUnitAccount ? (
    <Skeleton width="100%" />
  ) : (
    `${capitalizeFirstLetter(fromAccountType)} ${getLast4digits(fromAccountNumber)}`
  );

  const toTitle =
    selectedToAccount?.connectedType === COUNTERPARTY_CONNECTED_TYPE.PLAID
      ? selectedToAccount?.bank
      : selectedToAccount?.name;

  const toValue =
    selectedToAccount?.connectedType === COUNTERPARTY_CONNECTED_TYPE.PLAID
      ? `${capitalizeFirstLetter(selectedToAccount?.accountType)} ${getLast4digits(selectedToAccount?.accountNumber)}`
      : `${selectedToAccount?.bank} ${getLast4digits(selectedToAccount?.accountNumber)}`;

  return (
    <>
      <DialogContentStyled role="form">
        <ContainerGrid>
          <FocusGrid>
            <Wrapper spacing="40px">
              {isFetching ? (
                <LoadingContainerSpinner />
              ) : (
                <>
                  <Heading
                    family="secondary"
                    size="3xl"
                    weight="bold"
                  >{`How much are you sending to ${selectedToAccount?.name}?`}</Heading>
                  <Stack gap="16px">
                    <SendMoneyInput
                      addFunds={handleOpenDepositModal}
                      balance={inputBalanceAndLimit.balance}
                      dailyLimit={inputBalanceAndLimit.dailyLimit}
                      dailyRemainLimit={inputBalanceAndLimit.dailyRemainLimit}
                      defaultValue={amount}
                      fee={wireOutgoingFee}
                      handleDataChange={setMoneyInputState}
                      inputType={inputBalanceAndLimit.inputType}
                      monthlyLimit={inputBalanceAndLimit.monthlyLimit}
                      monthlyRemainLimit={inputBalanceAndLimit.monthlyRemainLimit}
                      ref={moneyInputRef}
                      title="Transfer Amount"
                    />
                    <ViewAllTransactionLimitButton />
                  </Stack>
                  <Stack spacing="8px">
                    <Text
                      color="primary"
                      size="md"
                      weight="medium"
                    >
                      From
                    </Text>
                    <FieldContainerStyled>
                      <FieldReadView
                        title={fromTitle}
                        value={fromValue}
                      />
                    </FieldContainerStyled>
                  </Stack>
                  <Stack spacing="8px">
                    <Text
                      color="primary"
                      size="md"
                      weight="medium"
                    >
                      To
                    </Text>
                    <FieldContainerStyled>
                      <FieldWithEditable
                        onEdit={editToSectionHandler}
                        title={toTitle}
                        value={toValue}
                      />
                    </FieldContainerStyled>
                  </Stack>
                  <Stack spacing="16px">
                    <Text
                      color="primary"
                      size="md"
                      weight="medium"
                    >
                      Transfer type
                    </Text>
                    <ReadOrEdit
                      customStyle={
                        isDisableWireInZBApp && isWireTypeOnly
                          ? {
                              opacity: 0.5
                            }
                          : null
                      }
                      onEditHandler={
                        isDisableWireInZBApp && isWireTypeOnly ? null : setIsEditHandlerTransferType
                      }
                      title={defaultTransferTypeOption.value}
                      value={defaultTransferTypeOption.title}
                    >
                      <RadioGroupStyled
                        aria-label="Transfer Type"
                        name="transfer-type-radio-buttons-group"
                        onChange={handleOnChangeType}
                        value={selectedType}
                      >
                        {transferOptions.map(({ value, label, content, isSelectable }) => (
                          <Box key={value}>
                            <FormControlLabelStyled
                              control={<Radio />}
                              disabled={!isSelectable}
                              label={label}
                              sx={{
                                opacity: isSelectable ? 1 : 0.5
                              }}
                              value={value}
                            />
                            {content}
                          </Box>
                        ))}
                      </RadioGroupStyled>
                      {isChaseBank && (
                        <DisableWireBoxStyled>
                          <Text
                            size="md"
                            weight="medium"
                          >
                            Sending a wire?
                            <Text
                              size="md"
                              sx={{ fontWeight: '400' }}
                            >
                              {' '}
                              To send wire transfers to a Chase Bank account, you must{' '}
                              <LinkStyled
                                color="primary.dark"
                                onClick={openAddPayeeModal}
                              >
                                add the account as a payee.
                              </LinkStyled>
                            </Text>
                          </Text>
                        </DisableWireBoxStyled>
                      )}
                      <Stack mt="16px">
                        {!isACHSupported && isWireSupported && (
                          <ACHAlert handleNavigate={handleNavigateToSpecificPayee} />
                        )}
                      </Stack>
                    </ReadOrEdit>
                    {isShowMoneyAlert && <MoneyAlert />}
                    {isWireTypeSelected && <WireAlert />}
                  </Stack>
                  {selectedType === TRANSFER_TYPE.WIRE.value ? (
                    <WireTransactionDescription
                      disabled={updateWireLoading}
                      maxLength={50}
                      onBlur={blurHandler}
                      placeholder="Add a note for the recipient"
                      title="Transaction Description (Required)"
                      value={description}
                    />
                  ) : (
                    <TransactionDescription
                      disabled={updateWireLoading}
                      maxLength={50}
                      onBlur={blurHandler}
                      placeholder="Add a note for the recipient"
                      title="Transaction Description (optional)"
                      value={description}
                    />
                  )}
                </>
              )}
            </Wrapper>
          </FocusGrid>
        </ContainerGrid>
      </DialogContentStyled>
      {!isFetching && (
        <ActionButtonsGrid>
          <LoadingButton
            color="primary"
            disabled={isDisabledReviewTransfer || (isDisableWireInZBApp && isWireTypeOnly)}
            fullWidth
            id={reviewButtonId}
            loading={isLoading}
            onClick={isWireTypeSelected ? handleVerifyAccount : handleReviewTransfer}
            size="large"
            variant="contained"
          >
            {isWireTypeSelected ? 'Next' : 'Review transfer'}
          </LoadingButton>
        </ActionButtonsGrid>
      )}
    </>
  );
}

HowMuchForm.propTypes = {
  handleClose: PropTypes.func,
  handleOpenDepositModal: PropTypes.func,
  openAddPayeeModal: PropTypes.func
};
